import { jsx as _jsx } from "react/jsx-runtime";
import { TabsAction } from '#pplx/frontend/aether/components/Tabs/TabsAction.js';
import { TabsActions } from '#pplx/frontend/aether/components/Tabs/TabsActions.js';
import { TabsContext } from '#pplx/frontend/aether/components/Tabs/TabsContext.js';
import { TabsHeader } from '#pplx/frontend/aether/components/Tabs/TabsHeader.js';
import { TabsList } from '#pplx/frontend/aether/components/Tabs/TabsList.js';
import { TabsPanel } from '#pplx/frontend/aether/components/Tabs/TabsPanel.js';
import { TabsTab } from '#pplx/frontend/aether/components/Tabs/TabsTab.js';
import { useControllableValue } from '#pplx/frontend/aether/internal/useControllableValue.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { asAutoInstrumented } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { useCallback, useEffect, useRef } from 'react';
const TAB_DEFAULT_TRACK_OPTIONS = {
    event: 'tab selected',
    data: asAutoInstrumented(),
};
function TabsRoot(props) {
    const { children, onValueChange, track: trackProp, animation = 'none', ...rest } = props;
    const __radixProps = useRadixProps(rest);
    const { track } = useInstrumentation();
    const controlledValue = props.value;
    const defaultValue = 'defaultValue' in props ? props.defaultValue : undefined;
    const initialValue = 'defaultValue' in props ? props.defaultValue : props.value;
    const [currentValue, setCurrentValue] = useControllableValue({
        value: controlledValue,
        defaultValue: initialValue,
        onValueChange,
    });
    // Controlled Radix tabs can report the same next value more than once before the
    // parent commits it. Remember it locally so one selection emits analytics once.
    const lastSelectionValueRef = useRef(currentValue);
    lastSelectionValueRef.current = currentValue;
    // Store updateIndicator callback from TabsList
    const updateIndicatorRef = useRef(() => { });
    // Wrap onValueChange - update indicator for both controlled and uncontrolled
    const handleValueChange = useCallback((value) => {
        // Radix reports plain strings here. This cast only narrows the root value/callback;
        // Tab and Panel child values are not statically checked against TValue.
        const nextValue = value;
        if (currentValue !== nextValue && lastSelectionValueRef.current !== nextValue) {
            const selectionTrack = typeof trackProp === 'function'
                ? trackProp({ value: nextValue, previousValue: currentValue })
                : trackProp;
            if (selectionTrack !== false) {
                track(selectionTrack ?? TAB_DEFAULT_TRACK_OPTIONS);
            }
            lastSelectionValueRef.current = nextValue;
        }
        setCurrentValue(nextValue);
        // Wait for DOM to update before measuring
        window.requestAnimationFrame(() => {
            updateIndicatorRef.current();
        });
    }, [currentValue, setCurrentValue, track, trackProp]);
    // Update indicator when value changes programmatically
    useEffect(() => {
        if (controlledValue !== undefined) {
            // Wait for DOM to update before measuring
            window.requestAnimationFrame(() => {
                updateIndicatorRef.current();
            });
        }
    }, [controlledValue]);
    return (_jsx(TabsContext.Provider, { value: {
            currentValue,
            updateIndicatorRef,
            animation,
        }, children: _jsx(TabsPrimitive.Root, { value: controlledValue, defaultValue: defaultValue, orientation: "horizontal", onValueChange: handleValueChange, 
            // Use 'contents' to make Tabs wrapper transparent to layout.
            // This allows parent components to control layout and scroll behavior,
            // while Tabs only handles state management and tab switching.
            className: animation === 'none' ? 'contents' : 'grid w-full min-w-0 grid-cols-[minmax(0,1fr)]', ...__radixProps, children: children }) }));
}
export const Tabs = Object.assign(TabsRoot, {
    Action: TabsAction,
    Actions: TabsActions,
    Header: TabsHeader,
    List: TabsList,
    Tab: TabsTab,
    Panel: TabsPanel,
});
//# sourceMappingURL=index.js.map